import React, {useEffect, useState} from "react";
import {Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import {Blue4, Grey6, Grey17, Red3} from "../../theme/styles";

/**
 * What a browser can draw in the page, and so what is offered a Preview.
 *
 * ONLY THESE, and the rest get no button rather than a dialog that says "cannot be previewed": a control
 * that opens onto nothing is a no wearing a yes. TIFF and HEIC are images a browser cannot draw; Office
 * files would need a converter, which nothing here has; HTML is left out on purpose -- drawn, it is a page
 * from the bucket's origin running its own script.
 */
export type PreviewKind = "pdf" | "image" | "video" | "audio" | "text";

const BY_EXTENSION: Record<string, PreviewKind> = {
    pdf: "pdf",
    png: "image", jpg: "image", jpeg: "image", gif: "image", bmp: "image", webp: "image", svg: "image",
    mp4: "video", webm: "video",
    mp3: "audio", wav: "audio", m4a: "audio", aac: "audio", ogg: "audio",
    txt: "text", md: "text", csv: "text", tsv: "text", log: "text", json: "text", xml: "text", yaml: "text",
    yml: "text", sql: "text",
};

export function previewKindFor(name?: string | null, mimeType?: string | null): PreviewKind | null {
    const type = (mimeType ?? "").toLowerCase().split(";")[0].trim();
    if (type === "application/pdf") return "pdf";
    if (["image/png", "image/jpeg", "image/gif", "image/bmp", "image/webp", "image/svg+xml"].includes(type)) return "image";
    if (type === "video/mp4" || type === "video/webm") return "video";
    if (type.startsWith("audio/")) return "audio";
    if (type === "text/plain" || type === "text/csv" || type === "text/markdown" || type === "application/json") return "text";
    // A useless or unrecognised type falls back to the extension, as fileTypeFor does.
    const dot = (name ?? "").lastIndexOf(".");
    if (dot > 0 && dot < (name ?? "").length - 1) return BY_EXTENSION[(name ?? "").slice(dot + 1).toLowerCase()] ?? null;
    return null;
}

// A preview is for reading, not for loading a log file the size of a disk into a <pre>.
const TEXT_LIMIT = 2 * 1024 * 1024;

type Props = {
    name: string;
    kind: PreviewKind;
    /** Mints the preview URL -- and, on the server, writes the PREVIEW row on the file's audit trail. */
    fetchUrl: () => Promise<{url: string; name?: string; note?: string | null}>;
    onDownload: () => void;
    onClose: () => void;
};

/**
 * One file, drawn in the page.
 *
 * THE BYTES COME STRAIGHT FROM S3 on a short-lived presigned URL served inline, exactly as a download's do
 * -- nothing goes through a function. The URL is asked for when the dialog opens and not before, because
 * asking for it is what records the preview: a list that pre-fetched URLs would put a preview on the trail
 * of every file somebody scrolled past.
 *
 * WHAT IS SHOWN IS WHAT A DOWNLOAD WOULD GIVE, stamp included. A clean PDF on a controlled document is
 * served as its DRAFT / UNCONTROLLED COPY rendition here too; a preview that showed the unstamped original
 * would be the one way to read a controlled file without the mark that says what it is.
 */
export default function AttachmentPreviewDialog({name, kind, fetchUrl, onDownload, onClose}: Props) {
    const [url, setUrl] = useState<string | null>(null);
    const [shownName, setShownName] = useState(name);
    const [note, setNote] = useState<string | null>(null);
    const [text, setText] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        fetchUrl()
            .then(async answer => {
                if (cancelled) return;
                setShownName(answer.name || name);
                setNote(answer.note ?? null);
                if (kind === "text") {
                    const reply = await fetch(answer.url);
                    if (!reply.ok) throw new Error("The file could not be read.");
                    const body = await reply.text();
                    if (!cancelled) setText(body.length > TEXT_LIMIT ? `${body.slice(0, TEXT_LIMIT)}\n\n… the rest is not shown here; download the file to read all of it.` : body);
                }
                if (!cancelled) setUrl(answer.url);
            })
            .catch((problem: any) => {
                if (!cancelled) setError(problem?.response?.data?.Cause ?? problem?.message ?? "This file could not be previewed.");
            });
        return () => { cancelled = true; };
        // Once per opening: a second URL would be a second preview on the trail.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const frame = {width: "100%", height: "75vh", border: "none", background: "#F5F5F5"};
    const body = error ? (
        <Typography style={{fontSize: 14, color: Red3}}>{error}</Typography>
    ) : !url ? (
        <div style={{display: "flex", justifyContent: "center", padding: 40}}><CircularProgress size={24}/></div>
    ) : kind === "pdf" ? (
        <iframe title={shownName} src={url} style={frame}/>
    ) : kind === "image" ? (
        <div style={{textAlign: "center", background: "#F5F5F5", padding: 8}}>
            <img src={url} alt={shownName} style={{maxWidth: "100%", maxHeight: "75vh", objectFit: "contain"}}/>
        </div>
    ) : kind === "video" ? (
        <video src={url} controls style={{width: "100%", maxHeight: "75vh"}}/>
    ) : kind === "audio" ? (
        <audio src={url} controls style={{width: "100%"}}/>
    ) : (
        <pre data-testid="attachment-preview-text"
             style={{margin: 0, maxHeight: "75vh", overflow: "auto", fontSize: 12.5, lineHeight: 1.5, whiteSpace: "pre-wrap",
                     wordBreak: "break-word", background: "#F5F5F5", padding: 12, color: Grey17}}>
            {text}
        </pre>
    );

    return (
        <Dialog open onClose={onClose} maxWidth="lg" fullWidth data-testid="attachment-preview">
            <DialogTitle style={{fontSize: 16, fontWeight: 600, color: Grey17, paddingBottom: 4}}>{shownName}</DialogTitle>
            <DialogContent>
                {note ? <Typography style={{fontSize: 12, color: Grey6, marginBottom: 8}}>{note}</Typography> : null}
                {body}
            </DialogContent>
            <DialogActions>
                <Button size="small" onClick={onDownload} startIcon={<DownloadIcon sx={{fontSize: 16}}/>}
                        sx={{textTransform: "none", color: Blue4}}>Download</Button>
                <Button size="small" onClick={onClose} sx={{textTransform: "none"}}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
